import { useEffect, useRef, useState } from "react";
import "./LpCarousel.css";

const SLIDE_MS = 3500; // 自動切り替え間隔
const PAUSE_AFTER_INTERACT = 6000; // ユーザー操作後の一時停止
const TRANSITION_MS = 600; // スクロールのアニメ時間の目安（CSS慣性と合わせる）

export default function LpCarousel() {
  const trackRef = useRef(null);
  const cardRefs = useRef([]);
  const [index, setIndex] = useState(0);
  const pauseRef = useRef(false);
  const timeoutRef = useRef(null);
  const interactTimerRef = useRef(null);

  const items = [
    {
      href: "https://linect-line.com/",
      img: "/images/Linect.jpg",
      title: "Linect",
      desc: "公式LINE導入・開発マーケティング支援",
    },
    {
      href: "https://rag.itlit.co.jp/",
      img: "/images/RAGonクラウド.jpg",
      title: "RAG on クラウド",
      desc: "生成AI導入・RAG開発",
    },
    {
      href: "https://itlit.co.jp/",
      img: "/images/ITLIT.jpg",
      title: "株式会社ITLIT",
      desc: "ホームページ",
    },
  ];

  // indexへスムース移動（ページ全体をスクロールさせない）
  const goTo = (i) => {
    const t = trackRef.current;
    const el = cardRefs.current[i];
    if (!t || !el) return;

    const left = el.offsetLeft - (t.offsetLeft || 0);
    t.scrollTo({ left, behavior: "smooth" });

    setIndex(i);
  };

  const next = () => goTo((index + 1) % items.length);
  const prev = () => goTo((index - 1 + items.length) % items.length);

  // オートプレイ
  useEffect(() => {
    if (pauseRef.current) return;
    timeoutRef.current = setTimeout(next, SLIDE_MS);
    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // ユーザー操作時に一時停止
  const pauseTemporarily = () => {
    pauseRef.current = true;
    clearTimeout(timeoutRef.current);
    clearTimeout(interactTimerRef.current);
    interactTimerRef.current = setTimeout(() => {
      pauseRef.current = false;
      timeoutRef.current = setTimeout(next, TRANSITION_MS);
    }, PAUSE_AFTER_INTERACT);
  };

  // ホイールやタッチで一時停止
  useEffect(() => {
    const t = trackRef.current;
    if (!t) return;
    const onInteract = () => pauseTemporarily();
    t.addEventListener("wheel", onInteract, { passive: true });
    t.addEventListener("touchstart", onInteract, { passive: true });
    t.addEventListener("keydown", onInteract);
    t.addEventListener(
      "scroll",
      () => {
        const tRect = t.getBoundingClientRect();
        let best = 0,
          min = Infinity;
        cardRefs.current.forEach((el, i) => {
          const diff = Math.abs(el.getBoundingClientRect().left - tRect.left);
          if (diff < min) {
            min = diff;
            best = i;
          }
        });
        setIndex(best);
      },
      { passive: true }
    );

    return () => {
      t.removeEventListener("wheel", onInteract);
      t.removeEventListener("touchstart", onInteract);
      t.removeEventListener("pointerdown", onInteract);
      t.removeEventListener("keydown", onInteract);
    };
  }, []);

  // 入力フォームにフォーカスが当たっている間は一時停止
  useEffect(() => {
    const onFocusIn = (e) => {
      if (
        e.target.closest?.(
          "input, select, textarea, [contenteditable=''], [contenteditable='true']"
        )
      ) {
        pauseTemporarily();
      }
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, []);

  return (
    <section
      className="lp-carousel-wrap is-fancy"
      aria-label="関連LP"
      onMouseEnter={pauseTemporarily}
      onMouseLeave={() => {
        pauseRef.current = false;
      }}
    >
      {/* エッジのグラデーション */}
      <div className="lp-fade lp-fade-left" aria-hidden="true" />
      <div className="lp-fade lp-fade-right" aria-hidden="true" />

      <div className="lp-carousel" ref={trackRef}>
        {items.map((it, i) => (
          <a
            key={i}
            ref={(el) => (cardRefs.current[i] = el)}
            className="lp-card"
            href={it.href}
            target="_blank"
            rel="noopener"
          >
            <div className="lp-card-media">
              <img src={it.img} alt={`${it.title}のバナー`} />
            </div>
            <div className="lp-card-body">
              <h3>{it.title}</h3>
              <p>{it.desc}</p>
            </div>
          </a>
        ))}
      </div>

      {/* 矢印 */}
      <button className="lp-nav prev" aria-label="前へ" onClick={prev}>
        ‹
      </button>
      <button className="lp-nav next" aria-label="次へ" onClick={next}>
        ›
      </button>

      {/* ドット */}
      <div className="lp-dots" role="tablist" aria-label="インジケータ">
        {items.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === index}
            aria-label={`${i + 1}枚目へ`}
            tabIndex={i === index ? 0 : -1}
            onClick={() => goTo(i)}
            className={i === index ? "is-active" : ""}
          />
        ))}
      </div>
    </section>
  );
}
